/**
 * The Agile Plan Notes editor's Markdown subset, parsed to and from the
 * editor's (TipTap/ProseMirror) document JSON.
 *
 * The subset is exactly what the Planner webapp writes and renders: `**bold**`,
 * `*italic*`, `[text](url)`, `- ` bullets and `1. ` numbered items, paragraphs
 * split by a blank line (a single newline is a line break inside a paragraph).
 * Bare URLs stay bare text.
 *
 * Why hand-written rather than a Markdown package: the subset is five
 * elements, a note must come back byte-for-byte when nothing was edited, and
 * `@tiptap/markdown` is not installed (it would also have to match our
 * @tiptap/core 3.20 and would add a dependency to the shared node_modules). A
 * small, fully tested parser/serializer keeps the output format under our
 * control (`**`, `*`, `- `, `1. `, never `__`, `_` or `* `).
 *
 * Only http, https and mailto hrefs become links; anything else stays text.
 */

export type NoteMark = { type: 'bold' } | { type: 'italic' } | { type: 'link'; attrs: { href: string } };
export type NoteInline =
  | { type: 'text'; text: string; marks?: NoteMark[] }
  | { type: 'hardBreak' };
type NoteParagraph = { type: 'paragraph'; content?: NoteInline[] };
type NoteListItem = { type: 'listItem'; content: NoteParagraph[] };
export type NoteBlock =
  | NoteParagraph
  | { type: 'bulletList'; content: NoteListItem[] }
  | { type: 'orderedList'; attrs?: { start?: number }; content: NoteListItem[] };
export type NoteDoc = { type: 'doc'; content: NoteBlock[] };

const SAFE_HREF = /^(https?:\/\/|mailto:)/i;
// A bare URL stops at whitespace, "<" and "*" (so a closing ** is not swallowed).
const BARE_URL = /^(?:https?:\/\/|www\.)[^\s<*]+/i;
const BULLET = /^[-*+]\s+(.*)$/;
const ORDERED = /^\d+[.)]\s+(.*)$/;
/** Characters a backslash escapes: the ones the parser would otherwise act on. */
const ESCAPABLE = '\\*_[]()';

// ---------------------------------------------------------------- parse

const markKey = (m: NoteMark) => (m.type === 'link' ? `link:${m.attrs.href}` : m.type);

function pushText(out: NoteInline[], text: string, marks: NoteMark[]) {
  if (!text) return;
  const last = out[out.length - 1];
  const same =
    last && last.type === 'text' &&
    (last.marks ?? []).map(markKey).join('|') === marks.map(markKey).join('|');
  if (same && last.type === 'text') last.text += text;
  else out.push(marks.length ? { type: 'text', text, marks: [...marks] } : { type: 'text', text });
}

/** The index of the `)` closing the `(` at `open`, counting nested pairs. */
function closingParen(s: string, open: number): number {
  let depth = 0;
  for (let i = open; i < s.length; i++) {
    if (/\s/.test(s[i])) return -1;
    if (s[i] === '(') depth++;
    else if (s[i] === ')' && --depth === 0) return i;
  }
  return -1;
}

/** The index of the `]` closing the `[` at `open`: nested pairs count, `\]` does not. */
function closingBracket(s: string, open: number): number {
  let depth = 0;
  for (let i = open; i < s.length; i++) {
    if (s[i] === '\\') { i++; continue; }
    if (s[i] === '[') depth++;
    else if (s[i] === ']' && --depth === 0) return i;
  }
  return -1;
}

const isWord = (c: string | undefined) => !!c && /[A-Za-z0-9]/.test(c);
const isSpace = (c: string | undefined) => c === undefined || /\s/.test(c);

type Item =
  | { t: 'text'; text: string }
  | { t: 'runs'; runs: NoteInline[] }
  | { t: 'delim'; ch: string; len: number; count: number; canOpen: boolean; canClose: boolean; active: boolean };

/** Tokenize one line of inline Markdown: text, link groups and `*` / `_` runs. */
function tokenize(s: string): Item[] {
  const items: Item[] = [];
  let plain = '';
  const flush = () => { if (plain) items.push({ t: 'text', text: plain }); plain = ''; };

  let i = 0;
  while (i < s.length) {
    const c = s[i];

    if (c === '\\' && i + 1 < s.length && ESCAPABLE.includes(s[i + 1])) {
      plain += s[i + 1];
      i += 2;
      continue;
    }

    if (i === 0 || !isWord(s[i - 1])) {
      const bare = BARE_URL.exec(s.slice(i, i + 2000));
      if (bare) { plain += bare[0]; i += bare[0].length; continue; }
    }

    if (c === '[') {
      const close = closingBracket(s, i);
      if (close > i + 1 && s[close + 1] === '(') {
        const end = closingParen(s, close + 1);
        const href = end > 0 ? s.slice(close + 2, end) : '';
        if (end > 0 && SAFE_HREF.test(href)) {
          flush();
          const runs: NoteInline[] = [];
          inlineRuns(s.slice(i + 1, close), [{ type: 'link', attrs: { href } }], runs);
          items.push({ t: 'runs', runs });
          i = end + 1;
          continue;
        }
      }
    }

    if (c === '*' || c === '_') {
      let j = i;
      while (s[j] === c) j++;
      const prev = s[i - 1];
      const next = s[j];
      // Left/right flanking, simplified: an opener is followed by a non-space
      // and a closer preceded by one. `_` is also kept out of words (snake_case).
      const left = !isSpace(next);
      const right = !isSpace(prev);
      const canOpen = left && (c === '*' || !isWord(prev));
      const canClose = right && (c === '*' || !isWord(next));
      flush();
      items.push({ t: 'delim', ch: c, len: j - i, count: j - i, canOpen, canClose, active: true });
      i = j;
      continue;
    }

    plain += c;
    i++;
  }
  flush();
  return items;
}

/** CommonMark-style emphasis matching over the `*` / `_` runs. Returns, per
 *  item index, the marks that wrap it. */
function resolveEmphasis(items: Item[]): NoteMark[][] {
  const spans: { open: number; close: number; mark: NoteMark }[] = [];

  for (let c = 0; c < items.length; c++) {
    const closer = items[c];
    if (closer.t !== 'delim' || !closer.canClose) continue;
    while (closer.count > 0) {
      let o = c - 1;
      for (; o >= 0; o--) {
        const cand = items[o];
        if (cand.t !== 'delim' || !cand.active || cand.ch !== closer.ch || !cand.canOpen || cand.count === 0) continue;
        // The "rule of 3": runs that can both open and close must not pair when
        // their lengths sum to a multiple of 3 (unless both are).
        if ((closer.canOpen || cand.canClose) && (cand.len + closer.len) % 3 === 0 &&
            !(cand.len % 3 === 0 && closer.len % 3 === 0)) continue;
        break;
      }
      if (o < 0) break;
      const opener = items[o] as Extract<Item, { t: 'delim' }>;
      const use = opener.count >= 2 && closer.count >= 2 ? 2 : 1;
      opener.count -= use;
      closer.count -= use;
      spans.push({ open: o, close: c, mark: { type: use === 2 ? 'bold' : 'italic' } });
      // Delimiters between a matched pair can no longer pair with anything.
      for (let k = o + 1; k < c; k++) {
        const between = items[k];
        if (between.t === 'delim') between.active = false;
      }
    }
  }

  return items.map((_, idx) => {
    const marks: NoteMark[] = [];
    for (const sp of spans) {
      if (sp.open < idx && idx < sp.close && !marks.some((m) => m.type === sp.mark.type)) marks.push(sp.mark);
    }
    return marks;
  });
}

/** Parse inline Markdown into runs, every run carrying `outer` marks. */
function inlineRuns(s: string, outer: NoteMark[], out: NoteInline[]): void {
  const items = tokenize(s);
  const wrap = resolveEmphasis(items);
  items.forEach((item, idx) => {
    const marks = [...outer];
    for (const m of wrap[idx]) if (!marks.some((x) => x.type === m.type)) marks.push(m);
    if (item.t === 'text') pushText(out, item.text, marks);
    else if (item.t === 'delim') pushText(out, item.ch.repeat(item.count), marks);
    else {
      for (const run of item.runs) {
        if (run.type !== 'text') { out.push(run); continue; }
        const merged = [...(run.marks ?? [])];
        for (const m of marks) if (!merged.some((x) => x.type === m.type && (m.type !== 'link' || markKey(x) === markKey(m)))) merged.push(m);
        pushText(out, run.text, merged);
      }
    }
  });
}

function paragraphOf(lines: string[]): NoteParagraph {
  const content: NoteInline[] = [];
  lines.forEach((line, n) => {
    if (n > 0) content.push({ type: 'hardBreak' });
    inlineRuns(line, [], content);
  });
  return content.length ? { type: 'paragraph', content } : { type: 'paragraph' };
}

/** Markdown (the Notes subset) to the editor's document JSON. */
export function parseNoteMarkdown(markdown: string): NoteDoc {
  const lines = (markdown ?? '')
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((l) => l.replace(/\s+$/, ''));
  const blocks: NoteBlock[] = [];
  let para: string[] = [];
  let list: { ordered: boolean; items: NoteListItem[] } | null = null;

  const flushPara = () => { if (para.length) blocks.push(paragraphOf(para)); para = []; };
  const flushList = () => {
    if (list) {
      blocks.push(list.ordered
        ? { type: 'orderedList', attrs: { start: 1 }, content: list.items }
        : { type: 'bulletList', content: list.items });
    }
    list = null;
  };

  for (const line of lines) {
    const trimmed = line.trimStart();
    const bullet = BULLET.exec(trimmed);
    const ordered = bullet ? null : ORDERED.exec(trimmed);
    if (!trimmed) { flushPara(); flushList(); continue; }
    if (bullet || ordered) {
      flushPara();
      const isOrdered = !!ordered;
      if (list && list.ordered !== isOrdered) flushList();
      if (!list) list = { ordered: isOrdered, items: [] };
      list.items.push({ type: 'listItem', content: [paragraphOf([(bullet ?? ordered)![1]])] });
      continue;
    }
    flushList();
    para.push(line);
  }
  flushPara();
  flushList();
  return { type: 'doc', content: blocks.length ? blocks : [{ type: 'paragraph' }] };
}

// ------------------------------------------------------------ serialize

const DELIM: Record<string, string> = { bold: '**', italic: '*' };
const CANONICAL = ['link', 'bold', 'italic'];

/** Percent-encode what would end a link early: spaces, and any parenthesis
 *  that has no partner (a balanced pair is left as it is). */
function encodeHref(href: string): string {
  const open: number[] = [];
  const bad = new Set<number>();
  [...href].forEach((ch, i) => {
    if (ch === '(') open.push(i);
    else if (ch === ')') { if (open.length) open.pop(); else bad.add(i); }
  });
  open.forEach((i) => bad.add(i));
  return [...href]
    .map((ch, i) => (ch === ' ' ? '%20' : bad.has(i) ? (ch === '(' ? '%28' : '%29') : ch))
    .join('');
}

type RunNode = { text: string; marks: NoteMark[] } | { hardBreak: true };

/** Text nodes with the whitespace at the edge of a marked run moved outside
 *  the marks that END there (marks that continue into the neighbouring node
 *  keep it); a run of only whitespace keeps just the marks it shares with both
 *  neighbours. */
function runsOf(nodes: NoteInline[] | undefined): RunNode[] {
  type T = { text: string; marks: NoteMark[] };
  const list: Array<T | null> = (nodes ?? []).map((node) =>
    node.type !== 'text' ? null : {
      text: node.text,
      marks: (node.marks ?? [])
        .filter((m) => m.type === 'bold' || m.type === 'italic' || m.type === 'link')
        .sort((a, b) => CANONICAL.indexOf(a.type) - CANONICAL.indexOf(b.type)),
    });
  const shared = (a: T | null | undefined, b: T | null | undefined): NoteMark[] => {
    if (!a || !b) return [];
    const keys = new Set(b.marks.map(markKey));
    return a.marks.filter((m) => keys.has(markKey(m)));
  };
  const out: RunNode[] = [];
  list.forEach((node, idx) => {
    if (!node) { out.push({ hardBreak: true }); return; }
    if (!node.marks.length) { out.push(node); return; }
    const prev = list[idx - 1];
    const next = list[idx + 1];
    const lead = /^\s*/.exec(node.text)![0];
    const core = node.text.slice(lead.length).replace(/\s+$/, '');
    const trail = node.text.slice(lead.length + core.length);
    if (!core) {
      const nextKeys = new Set((next?.marks ?? []).map(markKey));
      const both = shared(node, prev).filter((m) => nextKeys.has(markKey(m)));
      out.push({ text: node.text, marks: both });
      return;
    }
    if (lead) out.push({ text: lead, marks: shared(node, prev) });
    out.push({ text: core, marks: node.marks });
    if (trail) out.push({ text: trail, marks: shared(node, next) });
  });
  return out;
}

function escapeText(text: string, inLabel: boolean, edgeStart: boolean, edgeEnd: boolean): string {
  const chars = [...text];
  return chars
    .map((ch, i) => {
      if (ch === '\\') {
        // A backslash only needs doubling where it would escape the next character.
        return i + 1 < chars.length && ESCAPABLE.includes(chars[i + 1]) ? '\\\\' : ch;
      }
      // Inside a link label every `*` is escaped (a pair would read back as emphasis).
      if (ch === '*' && (inLabel || (i === 0 && edgeStart) || (i === chars.length - 1 && edgeEnd))) return '\\*';
      if (inLabel && (ch === '[' || ch === ']')) return `\\${ch}`;
      return ch;
    })
    .join('');
}

function serializeInline(nodes: NoteInline[] | undefined): string {
  const runs = runsOf(nodes);
  let out = '';
  let stack: NoteMark[] = [];
  const close = (mark: NoteMark) => {
    out += mark.type === 'link' ? `](${encodeHref(mark.attrs.href)})` : DELIM[mark.type];
  };
  const open = (mark: NoteMark) => {
    out += mark.type === 'link' ? '[' : DELIM[mark.type];
  };

  runs.forEach((run, idx) => {
    if ('hardBreak' in run) {
      for (let k = stack.length - 1; k >= 0; k--) close(stack[k]);
      stack = [];
      out += '\n';
      return;
    }
    const want = new Set(run.marks.map(markKey));
    // Keep the open marks that still apply; close from the top down to the
    // first one that does not, then open what is new.
    let keep = 0;
    while (keep < stack.length && want.has(markKey(stack[keep]))) keep++;
    for (let k = stack.length - 1; k >= keep; k--) close(stack[k]);
    stack = stack.slice(0, keep);
    const have = new Set(stack.map(markKey));
    for (const m of run.marks) {
      if (!have.has(markKey(m))) { open(m); stack.push(m); }
    }
    const prev = runs[idx - 1];
    const next = runs[idx + 1];
    const marked = run.marks.length > 0;
    const edgeStart = marked || (!!prev && 'text' in prev && prev.marks.length > 0);
    const edgeEnd = marked || (!!next && 'text' in next && next.marks.length > 0);
    out += escapeText(run.text, run.marks.some((m) => m.type === 'link'), edgeStart, edgeEnd);
  });
  for (let k = stack.length - 1; k >= 0; k--) close(stack[k]);
  return out.replace(/[ \t]+\n/g, '\n').replace(/\s+$/, '');
}

function serializeList(block: Extract<NoteBlock, { content: NoteListItem[] }>): string {
  const ordered = block.type === 'orderedList';
  return block.content
    .map((item, n) => {
      const text = serializeInline(item.content[0]?.content).replace(/\n/g, ' ');
      return `${ordered ? `${n + 1}.` : '-'} ${text}`.replace(/\s+$/, '');
    })
    .join('\n');
}

/** The editor's document JSON to Markdown (the Notes subset). */
export function serializeNoteDoc(doc: { content?: unknown[] } | null | undefined): string {
  const parts: string[] = [];
  for (const block of (doc?.content ?? []) as NoteBlock[]) {
    if (block.type === 'paragraph') parts.push(serializeInline(block.content));
    else if (block.type === 'bulletList' || block.type === 'orderedList') parts.push(serializeList(block));
  }
  // An empty paragraph is a blank block, never extra blank lines in the note.
  return parts.filter((p, n) => p !== '' || n === 0 && parts.length === 1).join('\n\n').replace(/\s+$/, '');
}

/** Load Markdown into the editor's document model and serialize it again, with no edit between. */
export function roundTripNoteMarkdown(markdown: string): string {
  return serializeNoteDoc(parseNoteMarkdown(markdown));
}

// ---------------------------------------------------------------- paste

function inlineFromDom(node: Node, marks: NoteMark[], out: NoteInline[]): void {
  node.childNodes.forEach((child) => {
    if (child.nodeType === 3) {
      pushText(out, (child.textContent ?? '').replace(/\s+/g, ' '), marks);
      return;
    }
    if (child.nodeType !== 1) return;
    const el = child as HTMLElement;
    const tag = el.tagName.toLowerCase();
    if (tag === 'script' || tag === 'style' || tag === 'head') return;
    if (tag === 'br') { out.push({ type: 'hardBreak' }); return; }
    let next = marks;
    if (tag === 'strong' || tag === 'b') next = [...marks, { type: 'bold' }];
    else if (tag === 'em' || tag === 'i') next = [...marks, { type: 'italic' }];
    else if (tag === 'a') {
      const href = (el.getAttribute('href') ?? '').trim();
      if (SAFE_HREF.test(href)) next = [...marks, { type: 'link', attrs: { href } }];
    }
    inlineFromDom(el, next, out);
  });
}

function trimInline(content: NoteInline[]): NoteInline[] {
  const out = content.map((n) => ({ ...n }));
  const first = out[0];
  const last = out[out.length - 1];
  if (first?.type === 'text') first.text = first.text.replace(/^\s+/, '');
  if (last?.type === 'text') last.text = last.text.replace(/\s+$/, '');
  return out.filter((n) => n.type !== 'text' || n.text !== '');
}

function blocksFromDom(node: Node, out: NoteBlock[]): void {
  let inline: NoteInline[] = [];
  const flush = () => {
    const content = trimInline(inline);
    if (content.length) out.push({ type: 'paragraph', content });
    inline = [];
  };
  node.childNodes.forEach((child) => {
    if (child.nodeType !== 1) {
      if (child.nodeType === 3) pushText(inline, (child.textContent ?? '').replace(/\s+/g, ' '), []);
      return;
    }
    const el = child as HTMLElement;
    const tag = el.tagName.toLowerCase();
    if (tag === 'script' || tag === 'style' || tag === 'head') return;
    if (tag === 'ul' || tag === 'ol') {
      flush();
      const items: NoteListItem[] = [];
      el.querySelectorAll(':scope > li').forEach((li) => {
        const content: NoteInline[] = [];
        inlineFromDom(li, [], content);
        const trimmed = trimInline(content);
        items.push({ type: 'listItem', content: [trimmed.length ? { type: 'paragraph', content: trimmed } : { type: 'paragraph' }] });
      });
      if (items.length) {
        out.push(tag === 'ol'
          ? { type: 'orderedList', attrs: { start: 1 }, content: items }
          : { type: 'bulletList', content: items });
      }
      return;
    }
    if (/^(p|div|h[1-6]|blockquote|pre|section|article|li|tr)$/.test(tag)) {
      flush();
      const hasBlocks = el.querySelector('p,div,ul,ol,h1,h2,h3,h4,h5,h6');
      if (hasBlocks) blocksFromDom(el, out);
      else {
        const content: NoteInline[] = [];
        inlineFromDom(el, [], content);
        const trimmed = trimInline(content);
        if (trimmed.length) out.push({ type: 'paragraph', content: trimmed });
      }
      return;
    }
    // inline element at block level
    const holder: NoteInline[] = [];
    inlineFromDom({ childNodes: [el] } as unknown as Node, [], holder);
    holder.forEach((n) => (n.type === 'text' ? pushText(inline, n.text, n.marks ?? []) : inline.push(n)));
  });
  flush();
}

/** Pasted HTML to the Notes Markdown subset: bold, italic, links and lists
 *  survive; everything else keeps its text and drops its formatting. */
export function htmlToNoteMarkdown(html: string): string {
  const doc = new DOMParser().parseFromString(html ?? '', 'text/html');
  const blocks: NoteBlock[] = [];
  blocksFromDom(doc.body, blocks);
  return serializeNoteDoc({ content: blocks });
}
